import { useCallback, useEffect, useRef, useState } from 'react'
import { type NavLinkProps, useFetcher, useLoaderData, useNavigate } from 'react-router'

export interface CacheAdapter {
	getItem(key: string): unknown | Promise<unknown>
	setItem(key: string, value: unknown): unknown | Promise<unknown>
	removeItem(key: string): unknown | Promise<unknown>
}

type CacheClientLoaderOptions = {
	type?: 'swr' | 'normal'
	key?: string
	adapter?: CacheAdapter
}

type CachedLoaderEnvelope<T> = T & {
	serverData: T
	deferredServerData?: Promise<T>
	deferredServerDataGeneration?: Promise<number>
	key: string
}

type CachePrefetchResult = { ok: true; data: unknown } | { ok: false }

type CachePrefetchState = {
	generations: Map<string, number>
	inFlight: Map<string, { generation: number; promise: Promise<CachePrefetchResult> }>
	invalidatedKeys: Set<string>
	writtenAt: Map<string, number>
}

const CLIENT_CACHE_PREFETCH_PARAM = '_client_cache_prefetch'
const CLIENT_CACHE_PREFETCH_MAX_AGE_MS = 15_000
const cachePrefetchStates = new WeakMap<CacheAdapter, CachePrefetchState>()
let latestDelayedNavigation = 0

const memoryCache = new Map<string, unknown>()

let cache: CacheAdapter = {
	getItem: key => memoryCache.get(key),
	setItem: (key, value) => memoryCache.set(key, value),
	removeItem: key => memoryCache.delete(key),
}

function isWebStorage(adapter: CacheAdapter | Storage): adapter is Storage {
	return typeof Storage !== 'undefined' && adapter instanceof Storage
}

function adaptWebStorage(storage: Storage): CacheAdapter {
	return {
		getItem(key) {
			const value = storage.getItem(key)
			if (value === null) return undefined
			try {
				return JSON.parse(value) as unknown
			} catch {
				return value
			}
		},
		setItem: (key, value) => storage.setItem(key, JSON.stringify(value)),
		removeItem: key => storage.removeItem(key),
	}
}

export function configureGlobalCache(createAdapter: () => CacheAdapter | Storage) {
	if (typeof document === 'undefined') return

	const adapter = createAdapter()
	cache = isWebStorage(adapter) ? adaptWebStorage(adapter) : adapter
}

export function getClientCacheKey(url: URL) {
	if (url.searchParams.has(CLIENT_CACHE_PREFETCH_PARAM)) {
		const normalizedUrl = new URL(url)
		normalizedUrl.searchParams.delete(CLIENT_CACHE_PREFETCH_PARAM)
		return `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`
	}
	return `${url.pathname}${url.search}${url.hash}`
}

function getCachePrefetchState(adapter: CacheAdapter) {
	let state = cachePrefetchStates.get(adapter)
	if (!state) {
		state = { generations: new Map(), inFlight: new Map(), invalidatedKeys: new Set(), writtenAt: new Map() }
		cachePrefetchStates.set(adapter, state)
	}
	return state
}

function getCacheGeneration(state: CachePrefetchState, key: string) {
	return state.generations.get(key) ?? 0
}

function hasCachedData<T>(data: T | null | undefined): data is T {
	return data !== undefined && data !== null
}

async function readCachedItem<T>(adapter: CacheAdapter, state: CachePrefetchState, key: string) {
	const generation = getCacheGeneration(state, key)
	if (state.invalidatedKeys.has(key)) return undefined

	try {
		const data = (await adapter.getItem(key)) as T | null | undefined
		if (generation !== getCacheGeneration(state, key) || state.invalidatedKeys.has(key)) return undefined
		return hasCachedData(data) ? data : undefined
	} catch {
		// Browser storage is an optimization. A blocked IndexedDB adapter must not
		// prevent the route's authoritative loader from rendering the page.
		return undefined
	}
}

async function commitCachedItem<T>(
	adapter: CacheAdapter,
	state: CachePrefetchState,
	key: string,
	data: T,
	generation: number,
) {
	if (generation !== getCacheGeneration(state, key)) return { current: false, stored: false }

	let stored = false
	try {
		await adapter.setItem(key, data)
		stored = true
	} catch {
		// Keep serving the fresh loader result even when persistent storage is unavailable.
	}

	if (generation !== getCacheGeneration(state, key)) {
		if (stored) {
			try {
				await adapter.removeItem(key)
			} catch {
				// The invalidated-key tombstone below prevents this stale value from
				// being read again during the current browser session.
			}
		}
		return { current: false, stored: false }
	}

	if (stored) state.invalidatedKeys.delete(key)
	return { current: true, stored }
}

async function loadCurrentServerData<T>(
	serverLoader: () => Promise<unknown>,
	state: CachePrefetchState,
	key: string,
): Promise<{ data: T; generation: number }> {
	for (;;) {
		const generation = getCacheGeneration(state, key)
		try {
			const data = (await serverLoader()) as T
			if (generation === getCacheGeneration(state, key)) return { data, generation }
		} catch (error) {
			if (generation === getCacheGeneration(state, key)) throw error
		}
	}
}

function toCachedLoaderEnvelope<T>(
	data: T | undefined,
	key: string,
	deferredServerData?: Promise<T>,
	deferredServerDataGeneration?: Promise<number>,
): CachedLoaderEnvelope<T> {
	return {
		...((data ?? {}) as T),
		serverData: data as T,
		deferredServerData,
		deferredServerDataGeneration,
		key,
	}
}

function invalidatePrefetchState(adapter: CacheAdapter, keys: readonly string[]) {
	const state = getCachePrefetchState(adapter)
	for (const key of keys) {
		state.generations.set(key, getCacheGeneration(state, key) + 1)
		state.invalidatedKeys.add(key)
		state.writtenAt.delete(key)
	}
}

async function removeCachedItems(adapter: CacheAdapter, keys: readonly string[]) {
	invalidatePrefetchState(adapter, keys)
	// A cache failure after a successful action must never make the mutation look
	// failed and invite a duplicate submission. Tombstones keep failed removals unreadable.
	await Promise.allSettled(keys.map(async key => adapter.removeItem(key)))
}

export function getClientCachePrefetchUrl(to: string) {
	const url = new URL(to, 'https://client-cache.local')
	url.searchParams.set(CLIENT_CACHE_PREFETCH_PARAM, '1')
	return `${url.pathname}${url.search}${url.hash}`
}

type PrefetchOnHoverOptions = Pick<
	NavLinkProps,
	| 'onClick'
	| 'onFocus'
	| 'onPointerEnter'
	| 'target'
	| 'replace'
	| 'state'
	| 'preventScrollReset'
	| 'relative'
	| 'viewTransition'
>

export function usePrefetchOnHover(to: string | null, options: PrefetchOnHoverOptions = {}) {
	const fetcher = useFetcher()
	const load = fetcher.load
	const navigate = useNavigate()
	const prefetchPromiseRef = useRef<Promise<void> | null>(null)
	const pendingNavigationRef = useRef<number | null>(null)
	const pendingNavigationLocationRef = useRef<string | null>(null)

	const finishPrefetch = useCallback(
		(promise: Promise<void>) => {
			if (prefetchPromiseRef.current !== promise) return
			prefetchPromiseRef.current = null
			const navigationId = pendingNavigationRef.current
			pendingNavigationRef.current = null
			const navigationLocation = pendingNavigationLocationRef.current
			pendingNavigationLocationRef.current = null
			if (navigationId === null || navigationId !== latestDelayedNavigation) return
			if (navigationLocation !== window.location.href) return
			if (!to) return
			void navigate(to, {
				replace: options.replace,
				state: options.state,
				preventScrollReset: options.preventScrollReset,
				relative: options.relative,
				viewTransition: options.viewTransition,
			})
		},
		[
			navigate,
			options.preventScrollReset,
			options.relative,
			options.replace,
			options.state,
			options.viewTransition,
			to,
		],
	)
	const prefetch = useCallback(() => {
		if (!to || prefetchPromiseRef.current || fetcher.state !== 'idle') return
		const promise = load(getClientCachePrefetchUrl(to))
		prefetchPromiseRef.current = promise
		void promise.then(
			() => finishPrefetch(promise),
			() => finishPrefetch(promise),
		)
	}, [fetcher.state, finishPrefetch, load, to])

	return {
		onClick: event => {
			options.onClick?.(event)
			const isPlainLeftClick =
				event.button === 0 &&
				!event.metaKey &&
				!event.altKey &&
				!event.ctrlKey &&
				!event.shiftKey &&
				(!options.target || options.target === '_self')
			if (event.defaultPrevented || !isPlainLeftClick) return
			const navigationId = ++latestDelayedNavigation
			if (!prefetchPromiseRef.current) return
			// Navigating aborts fetcher loads. Let this intent request finish so the
			// route can consume the cache instead of issuing the same loader twice.
			event.preventDefault()
			pendingNavigationRef.current = navigationId
			pendingNavigationLocationRef.current = window.location.href
		},
		onFocus: event => {
			options.onFocus?.(event)
			if (!event.defaultPrevented) prefetch()
		},
		onPointerEnter: event => {
			options.onPointerEnter?.(event)
			if (!event.defaultPrevented) prefetch()
		},
	} satisfies Pick<NavLinkProps, 'onClick' | 'onFocus' | 'onPointerEnter'>
}

export async function cacheClientLoader<
	TArgs extends { url: URL; serverLoader: () => Promise<unknown> },
	TData = Awaited<ReturnType<TArgs['serverLoader']>>,
>({ url, serverLoader }: TArgs, options: CacheClientLoaderOptions = {}): Promise<CachedLoaderEnvelope<TData>> {
	const { type = 'swr', key = getClientCacheKey(url), adapter = cache } = options
	const prefetchState = getCachePrefetchState(adapter)
	const isIntentPrefetch = url.searchParams.get(CLIENT_CACHE_PREFETCH_PARAM) === '1'

	if (isIntentPrefetch) {
		const inFlight = prefetchState.inFlight.get(key)
		if (inFlight) {
			const result = await inFlight.promise
			if (result.ok && inFlight.generation === getCacheGeneration(prefetchState, key)) {
				return toCachedLoaderEnvelope(result.data as TData, key)
			}
		}

		const existingData = await readCachedItem<TData>(adapter, prefetchState, key)
		if (hasCachedData(existingData)) {
			// Intent only needs to fill a missing cache. A cached route can navigate
			// immediately and let its normal SWR load refresh in the background.
			return toCachedLoaderEnvelope(existingData, key)
		}

		const generation = getCacheGeneration(prefetchState, key)
		const promise: Promise<CachePrefetchResult> = serverLoader().then(
			async freshData => {
				const result = await commitCachedItem(adapter, prefetchState, key, freshData, generation)
				if (!result.current || !result.stored || generation !== getCacheGeneration(prefetchState, key)) {
					return { ok: false }
				}
				prefetchState.writtenAt.set(key, Date.now())
				return { ok: true, data: freshData }
			},
			() => ({ ok: false }),
		)
		const entry = { generation, promise }
		prefetchState.inFlight.set(key, entry)
		const result = await promise.finally(() => {
			if (prefetchState.inFlight.get(key) === entry) prefetchState.inFlight.delete(key)
		})
		return toCachedLoaderEnvelope(result.ok ? (result.data as TData) : existingData, key)
	}

	const existingData = await readCachedItem<TData>(adapter, prefetchState, key)
	const pendingPrefetch = prefetchState.inFlight.get(key)
	if (pendingPrefetch) {
		if (hasCachedData(existingData)) {
			const deferredResult = pendingPrefetch.promise.then(async result => {
				if (result.ok && pendingPrefetch.generation === getCacheGeneration(prefetchState, key)) {
					prefetchState.writtenAt.delete(key)
					return { data: result.data as TData, generation: pendingPrefetch.generation }
				}
				return loadCurrentServerData<TData>(serverLoader, prefetchState, key)
			})
			return toCachedLoaderEnvelope(
				existingData,
				key,
				deferredResult.then(result => result.data),
				deferredResult.then(result => result.generation),
			)
		}
		const result = await pendingPrefetch.promise
		if (result.ok && pendingPrefetch.generation === getCacheGeneration(prefetchState, key)) {
			prefetchState.writtenAt.delete(key)
			return toCachedLoaderEnvelope(result.data as TData, key)
		}
	}

	const writtenAt = prefetchState.writtenAt.get(key)
	const hasFreshPrefetch =
		hasCachedData(existingData) && writtenAt !== undefined && Date.now() - writtenAt <= CLIENT_CACHE_PREFETCH_MAX_AGE_MS
	if (hasFreshPrefetch) {
		prefetchState.writtenAt.delete(key)
		return toCachedLoaderEnvelope(existingData, key)
	}
	if (writtenAt !== undefined) prefetchState.writtenAt.delete(key)

	if (type === 'normal' && hasCachedData(existingData)) {
		return toCachedLoaderEnvelope(existingData, key)
	}

	if (!hasCachedData(existingData)) {
		for (;;) {
			const result = await loadCurrentServerData<TData>(serverLoader, prefetchState, key)
			const commit = await commitCachedItem(adapter, prefetchState, key, result.data, result.generation)
			if (commit.current && result.generation === getCacheGeneration(prefetchState, key)) {
				return toCachedLoaderEnvelope(result.data, key)
			}
		}
	}

	const deferredResult = loadCurrentServerData<TData>(serverLoader, prefetchState, key)
	return toCachedLoaderEnvelope(
		existingData,
		key,
		deferredResult.then(result => result.data),
		deferredResult.then(result => result.generation),
	)
}

export async function decacheClientLoader<TData>(
	{ url, serverAction }: { url: URL; serverAction: () => Promise<TData> },
	options: Pick<CacheClientLoaderOptions, 'key' | 'adapter'> = {},
) {
	const { key = getClientCacheKey(url), adapter = cache } = options
	const data = await serverAction()
	await removeCachedItems(adapter, [key])
	return data
}

export async function invalidateCache(key: string | readonly string[]) {
	const keys: readonly string[] = typeof key === 'string' ? [key] : key
	await removeCachedItems(cache, keys)
}

export function useCacheInvalidator() {
	return { invalidateCache }
}

export function useCachedLoaderData<T>({ adapter = cache }: { adapter?: CacheAdapter } = {}) {
	const loaderData = useLoaderData() as CachedLoaderEnvelope<T>
	const navigate = useNavigate()
	const serverData = loaderData.serverData ?? loaderData
	const cacheKey = loaderData.key
	const [freshResult, setFreshResult] = useState<{
		key: string
		promise: Promise<T>
		data: T
	} | null>(null)

	useEffect(() => {
		let isMounted = true
		if (loaderData.deferredServerData && cacheKey) {
			const deferredServerData = loaderData.deferredServerData
			const generation =
				loaderData.deferredServerDataGeneration ??
				Promise.resolve(getCacheGeneration(getCachePrefetchState(adapter), cacheKey))
			void Promise.all([deferredServerData, generation])
				.then(async ([newData, expectedGeneration]) => {
					const result = await commitCachedItem(
						adapter,
						getCachePrefetchState(adapter),
						cacheKey,
						newData,
						expectedGeneration,
					)
					if (
						!isMounted ||
						!result.current ||
						expectedGeneration !== getCacheGeneration(getCachePrefetchState(adapter), cacheKey)
					) {
						return
					}
					setFreshResult({ key: cacheKey, promise: deferredServerData, data: newData })
				})
				.catch(error => {
					if (!isMounted) return
					if (error instanceof Response && error.status === 302) {
						const location = error.headers.get('Location')
						if (location) void navigate(location)
						return
					}
					// Keep the cached snapshot. Throwing from a detached promise only
					// creates an unhandled rejection and cannot reach React's error boundary.
				})
		}

		return () => {
			isMounted = false
		}
	}, [adapter, cacheKey, loaderData.deferredServerData, loaderData.deferredServerDataGeneration, navigate])

	const freshData =
		freshResult !== null &&
		cacheKey !== undefined &&
		freshResult.key === cacheKey &&
		freshResult.promise === loaderData.deferredServerData
			? freshResult.data
			: serverData

	return {
		...loaderData,
		...freshData,
		cacheKey,
		invalidate: () => (cacheKey ? removeCachedItems(adapter, [cacheKey]) : Promise.resolve()),
	} as T & { cacheKey?: string; invalidate: () => unknown | Promise<unknown> }
}
